import { useState } from 'react';
import { Thumb } from '../components/ImagePick';
import { sotd as Api, type SotdNote } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { Shim, SkeletonRows } from '../components/Shimmer';
import { useSelection, TickAll, Tick, BulkActions } from '../components/Bulk';
import { SotdForm } from './SotdForm';

const day = (v: string) => {
  const d = new Date(`${v}T00:00:00Z`);
  return isNaN(d.valueOf()) ? v
    : d.toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', timeZone: 'UTC' });
};

export function Sotd() {
  const { data, error, loading, reload } = useAsync((signal) => Api.list(signal));
  const [editing, setEditing] = useState<SotdNote | 'new' | null>(null);

  const notes = data?.notes ?? [];
  const today = new Date().toISOString().slice(0, 10);
  const haveToday = notes.some((n) => n.note_date === today && n.state === 'published');

  const sel = useSelection(notes);

  return (
    <div className="pg">
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 29 }}>Stocks of the day</h1>
          <p style={{ fontSize: 13.5, color: 'var(--tx2)', marginTop: 7 }}>
            {loading && !data ? <Shim w={210} h={12} style={{ marginTop: 3 }} />
              : haveToday ? 'Today’s note is published.'
              : 'Today’s note has not been published yet.'}
          </p>
        </div>
        <button className="b bg" type="button" onClick={() => setEditing('new')}>New note</button>
      </div>

      {error ? <div className="err">Could not load the notes — {error.message}</div> : null}

      <div className="crd" style={{ padding: '20px 22px' }}>
        <BulkActions
          path="/sotd" noun="note"
          rows={sel.picked.map((n) => ({ id: n.id, label: String(n.headline || '—') }))}
          actions={[['publish', 'Publish'], ['unpublish', 'Unpublish']]}
          onDone={async () => { sel.clear(); await reload(); }}
        />

        <div className="tblx">
          <table className="tbl">
            <thead>
              <tr>
                <TickAll on={sel.allOn} some={sel.picked.length > 0} onChange={sel.toggleAll} /><th>Date</th><th>Headline</th><th>Levels</th><th>Watchlist</th><th>State</th><th /></tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <SkeletonRows cols={7} tick />
              ) : notes.length === 0 ? (
                <tr><td colSpan={7} className="muted" style={{ padding: '26px 8px' }}>
                  No morning notes yet. A published note needs News access on the day, and is free once it is a day old.
                </td></tr>
              ) : notes.map((n) => (
                <tr key={n.id} style={sel.isOn(n.id) ? { background: 'rgba(var(--goldrgb), .05)' } : undefined}>
                  <Tick on={sel.isOn(n.id)} label={String(n.headline || '—')}
                    onChange={(v) => sel.toggle(n.id, v)} />
                  <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{day(n.note_date)}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <Thumb src={n.chart_path} width={60} />
                      <div style={{ minWidth: 0 }}>
                        <div>{n.headline || '—'}</div>
                        {n.note ? (
                          <div style={{ fontSize: 11.5, color: 'var(--tx3)', maxWidth: 420, overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.note}</div>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  {/* Three numbers on one line. Left to wrap, this column alone
                      made every row three lines tall. */}
                  <td className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {[n.support && `S ${n.support}`, n.pivot && `P ${n.pivot}`, n.resistance && `R ${n.resistance}`]
                      .filter(Boolean).join(' · ') || '—'}
                  </td>
                  <td className="muted">{n.watch_count ?? n.watchlist?.length ?? 0}</td>
                  <td>
                    <span className="tg" style={{
                      background: n.state === 'published' ? 'rgba(62,207,142,.14)' : 'rgba(var(--wash), calc(.06 * var(--washx)))',
                    }}>{n.state}</span>
                  </td>
                  <td className="right">
                    <button className="b bo" style={{ padding: '6px 12px', fontSize: 11.5 }}
                      type="button" onClick={() => setEditing(n)}>Edit</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing ? (
        <SotdForm
          note={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await reload(); }}
        />
      ) : null}
    </div>
  );
}
